import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { getPosts, reorderPosts } from '../services/api';
import { FileText, Loader2, ShoppingCart, DollarSign, FolderSearch, GripVertical } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import CustomOrderModal from '../components/CustomOrderModal';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const PublicCollectionsPage = () => {
  const navigate = useNavigate();
  const { isOwner } = useAuth();
  const [storePosts, setStorePosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderOpen, setOrderOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const posts = await getPosts(null, true);
      posts.sort((a, b) => {
        const aOrder = a.sort_order_store != null ? a.sort_order_store : 999999;
        const bOrder = b.sort_order_store != null ? b.sort_order_store : 999999;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return new Date(b.publishedAt) - new Date(a.publishedAt);
      });
      setStorePosts(posts);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setStorePosts(prev => {
      const oldIndex = prev.findIndex(p => p.id === active.id);
      const newIndex = prev.findIndex(p => p.id === over.id);
      const reordered = arrayMove(prev, oldIndex, newIndex);
      reorderPosts('store', reordered.map(p => p.id)).catch(() => {
        toast({ title: 'Error', description: 'Failed to save order', variant: 'destructive' });
      });
      return reordered;
    });
  }, []);

  const SortableStoreCard = ({ post }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: post.id });
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      zIndex: isDragging ? 10 : 'auto',
    };
    return (
      <div ref={setNodeRef} style={style} className="relative">
        {isOwner && (
          <div {...attributes} {...listeners} className="absolute top-3 right-3 z-20 bg-amber-700/90 text-black p-1.5 rounded cursor-grab active:cursor-grabbing" data-testid={`store-drag-handle-${post.id}`}>
            <GripVertical className="w-4 h-4" />
          </div>
        )}
        <StoreCard post={post} />
      </div>
    );
  };

  const StoreCard = ({ post }) => (
    <Card 
      className="overflow-hidden bg-gray-900/90 border-2 border-amber-900/30 hover:border-amber-600/50 cursor-pointer group transition-all hover:shadow-[0_0_20px_rgba(251,191,36,0.15)] h-full"
      onClick={() => navigate(`/post/${post.id}`)}
      data-testid={`store-post-${post.id}`}
    >
      <div className="relative h-48 overflow-hidden">
        {post.images?.[0] ? (
          <img src={post.images[0]} alt={post.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" style={{filter: 'sepia(0.2) contrast(1.05)'}} />
        ) : (
          <div className="w-full h-full bg-gray-800 flex items-center justify-center">
            <FileText className="w-12 h-12 text-gray-600" />
          </div>
        )}
        {post.price > 0 && (
          <div className="absolute bottom-2 right-2 bg-green-600 text-white px-3 py-1 text-sm font-headline tracking-wider flex items-center gap-1">
            <DollarSign className="w-4 h-4" />
            {post.price?.toFixed(2)}
          </div>
        )}
      </div>
      <CardContent className="p-4">
        <h3 className="font-headline text-lg text-gray-100 tracking-wide group-hover:text-amber-500 transition-colors mb-1">{post.title}</h3>
        <p className="text-gray-400 font-typewriter text-sm line-clamp-2">{post.content}</p>
      </CardContent>
    </Card>
  );

  return (
    <motion.div 
      className="min-h-screen bg-black relative py-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      data-testid="store-page"
    >
      <div className="absolute inset-0 opacity-5 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCI+PHBhdGggZD0iTTAgMGg2MHY2MEgweiIgZmlsbD0iIzAwMCIvPjxwYXRoIGQ9Ik0zMCAzMG0tMiAwYTIgMiAwIDEgMSA0IDBhMiAyIDAgMSAxLTQgMCIgZmlsbD0iI2ZmZiIvPjwvc3ZnPg==')]"></div>
      
      <div className="container mx-auto px-4 max-w-6xl relative z-10">
        <motion.div 
          className="mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <div className="flex items-center gap-3 mb-2">
            <ShoppingCart className="w-8 h-8 text-amber-500" />
            <h1 className="text-3xl font-headline text-amber-500 tracking-wider" data-testid="store-title">STORE</h1>
          </div>
          <p className="text-gray-400 font-typewriter">Missed out on a past case? Individual archived case file photosets available for purchase</p>
        </motion.div>

        {/* Custom Order Card — hidden (Stripe TOS concern), code preserved
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <Card
            className="overflow-hidden bg-gradient-to-r from-amber-950/40 via-gray-900/90 to-amber-950/40 border-2 border-amber-600/60 cursor-pointer group transition-all hover:border-amber-500 hover:shadow-[0_0_25px_rgba(251,191,36,0.2)]"
            onClick={() => setOrderOpen(true)}
            data-testid="custom-order-card"
          >
            <CardContent className="p-6 flex items-center gap-5">
              <div className="w-14 h-14 bg-amber-900/30 border-2 border-amber-700/50 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:border-amber-500 transition-colors">
                <FolderSearch className="w-7 h-7 text-amber-500" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-headline text-lg text-amber-400 tracking-wider group-hover:text-amber-300 transition-colors">REQUEST CUSTOM CASE</h3>
                <p className="text-gray-400 font-typewriter text-sm mt-1">Order a custom film or photoset tailored to your vision. Choose your model, describe the scene, and we'll bring it to life.</p>
              </div>
              <Button className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider flex-shrink-0 hidden sm:flex" data-testid="custom-order-open-btn">
                ORDER NOW
              </Button>
            </CardContent>
          </Card>
        </motion.div>
        */}

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-12 h-12 text-amber-500 animate-spin" data-testid="store-loading" />
          </div>
        ) : storePosts.length > 0 ? (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            {isOwner ? (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={storePosts.map(p => p.id)} strategy={rectSortingStrategy}>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {storePosts.map(post => (
                      <SortableStoreCard key={post.id} post={post} />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {storePosts.map(post => (
                  <StoreCard key={post.id} post={post} />
                ))}
              </div>
            )}
          </motion.div>
        ) : (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="bg-gray-900/90 border-2 border-amber-900/30">
              <CardContent className="py-12 text-center">
                <ShoppingCart className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 font-typewriter">No items available for purchase yet</p>
                <p className="text-gray-500 text-sm mt-2 font-typewriter">Check back soon for exclusive case files</p>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
      {/* CustomOrderModal hidden — code preserved
      <CustomOrderModal open={orderOpen} onOpenChange={setOrderOpen} />
      */}
    </motion.div>
  );
};

export default PublicCollectionsPage;
